import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { exportExcel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Info, FileText, Plus, Trash, PencilSimple, House, Hash, Buildings, Image as ImageIcon, Lock, EnvelopeSimple, Bell, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const { user, workspace } = useAuth();
  const [tax, setTax] = useState({ company_name: "", company_npwp: "", company_idtku: "", default_trx_code: "04", default_vat_rate: 12, use_dpp_nilai_lain: false, signer_name: "" });
  const emptyProfile = { company_name: "", company_address: "", company_phone: "", company_email: "", company_website: "", header_tagline: "", footer_text: "", company_bank: "", bank_account_name: "", bank_branch: "", signer_name: "", signer_title: "", paper_size: "A4", paper_orientation: "portrait", show_phone: true, show_email: true, show_website: true, show_address: true, show_tax_id: true, show_bank: true, show_footer: true, paper_config: {}, company_city: "", company_establishment_date: "", company_establishment_notary: "", company_sk_menkumham: "", business_activities: "", kbli_codes: [], directors: [], commissioners: [] };
  const [profile, setProfile] = useState(emptyProfile);
  const [logo, setLogo] = useState({ base64: "", mime: "", url: "" });
  const [sig, setSig] = useState({ base64: "", mime: "" });
  const [sigMaker, setSigMaker] = useState({ base64: "", mime: "" });
  const [sigWarehouse, setSigWarehouse] = useState({ base64: "", mime: "" });
  const logoInputRef = React.useRef(null);
  // warehouses
  const [warehouses, setWarehouses] = useState([]);
  const [whOpen, setWhOpen] = useState(false);
  const [whForm, setWhForm] = useState({ code: "", name: "", address: "", manager: "", is_default: false });
  const [whEdit, setWhEdit] = useState(null);
  // numbering
  const [numSeries, setNumSeries] = useState({});
  const [numLabels, setNumLabels] = useState({});
  const [numEditing, setNumEditing] = useState(null);
  const [numForm, setNumForm] = useState({ prefix: "", include_year: true, year_format: "YYYY", include_month: false, include_day: false, reset: "annual", padding: 5, start: 1 });

  useEffect(() => {
    api.get("/efaktur/settings").then((r) => setTax(r.data)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/numbering").then((r) => { setNumSeries(r.data.series); setNumLabels(r.data.labels); }).catch(() => {});
    loadProfile();
  }, []);

  const loadProfile = () => api.get("/company-profile").then((r) => {
    const d = r.data;
    setProfile({
      company_name: d.company_name || "",
      company_address: d.company_address || "",
      company_phone: d.company_phone || "",
      company_email: d.company_email || "",
      company_website: d.company_website || "",
      header_tagline: d.header_tagline || "",
      footer_text: d.footer_text || "",
      company_bank: d.company_bank || "",
      bank_account_name: d.bank_account_name || "",
      bank_branch: d.bank_branch || "",
      signer_name: d.signer_name || "",
      signer_title: d.signer_title || "",
      paper_size: d.paper_size || "A4",
      paper_orientation: d.paper_orientation || "portrait",
      show_phone: d.show_phone !== false,
      show_email: d.show_email !== false,
      show_website: d.show_website !== false,
      show_address: d.show_address !== false,
      show_tax_id: d.show_tax_id !== false,
      show_bank: d.show_bank !== false,
      show_footer: d.show_footer !== false,
      paper_config: d.paper_config || {},
      company_city: d.company_city || "",
      company_establishment_date: d.company_establishment_date || "",
      company_establishment_notary: d.company_establishment_notary || "",
      company_sk_menkumham: d.company_sk_menkumham || "",
      business_activities: d.business_activities || "",
      kbli_codes: Array.isArray(d.kbli_codes) ? d.kbli_codes : [],
      directors: Array.isArray(d.directors) ? d.directors : [],
      commissioners: Array.isArray(d.commissioners) ? d.commissioners : [],
    });
    setLogo({ base64: d.company_logo_base64 || "", mime: d.company_logo_mime || "image/png", url: d.company_logo_url || "" });
    setSig({ base64: d.signature_base64 || "", mime: d.signature_mime || "image/png" });
    setSigMaker({ base64: d.signature_maker_base64 || "", mime: d.signature_maker_mime || "image/png" });
    setSigWarehouse({ base64: d.signature_warehouse_base64 || "", mime: d.signature_warehouse_mime || "image/png" });
  });

  const saveProfile = async () => {
    try { await api.post("/company-profile", profile); toast.success("Profil perusahaan tersimpan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const addKbli = () => setProfile({ ...profile, kbli_codes: [...(profile.kbli_codes || []), { code: "", description: "" }] });
  const updKbli = (i, patch) => { const arr = [...(profile.kbli_codes || [])]; arr[i] = { ...arr[i], ...patch }; setProfile({ ...profile, kbli_codes: arr }); };
  const delKbli = (i) => { const arr = [...(profile.kbli_codes || [])]; arr.splice(i, 1); setProfile({ ...profile, kbli_codes: arr }); };

  const addPengurus = (key, defaultTitle) => setProfile({ ...profile, [key]: [...(profile[key] || []), { title: defaultTitle, name: "", ktp: "", address: "" }] });
  const updPengurus = (key, i, patch) => { const arr = [...(profile[key] || [])]; arr[i] = { ...arr[i], ...patch }; setProfile({ ...profile, [key]: arr }); };
  const delPengurus = (key, i) => { const arr = [...(profile[key] || [])]; arr.splice(i, 1); setProfile({ ...profile, [key]: arr }); };

  const uploadLogo = async (file) => {
    if (!file) return;
    const ok = ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type);
    if (!ok) return toast.error("Format harus PNG/JPG/WEBP");
    if (file.size > 1_000_000) return toast.error("Ukuran maks 1 MB");
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",", 2)[1]);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    try {
      await api.post("/company-profile/logo", { image_base64: b64, mime_type: file.type });
      toast.success("Logo tersimpan — semua PDF eksternal akan memakai logo ini");
      loadProfile();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Hapus logo?")) return;
    try { await api.delete("/company-profile/logo"); toast.success("Logo dihapus"); loadProfile(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const uploadSignature = async (file, role = "company") => {
    if (!file) return;
    const ok = ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type);
    if (!ok) return toast.error("Format harus PNG/JPG/WEBP");
    if (file.size > 500_000) return toast.error("Ukuran tanda tangan maks 500 KB");
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",", 2)[1]);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    try {
      await api.post("/company-profile/signature", { image_base64: b64, mime_type: file.type, role });
      toast.success("Tanda tangan tersimpan — akan otomatis muncul di PDF terkait");
      loadProfile();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); }
  };

  const removeSignature = async (role = "company") => {
    if (!window.confirm("Hapus tanda tangan digital ini?")) return;
    try { await api.delete(`/company-profile/signature?role=${role}`); toast.success("Tanda tangan dihapus"); loadProfile(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const SigBox = ({ sigState, role, label, desc, prefix }) => (
    <div className="md:col-span-2 flex items-start gap-4 bg-stone-50 border border-stone-200 rounded-md p-3" data-testid={`${prefix}-box`}>
      <div className="w-40 h-20 bg-white border border-dashed border-stone-300 rounded flex items-center justify-center overflow-hidden shrink-0">
        {sigState.base64 ? (
          <img src={`data:${sigState.mime || "image/png"};base64,${sigState.base64}`} alt={label} className="max-w-full max-h-full object-contain" data-testid={`${prefix}-preview`} />
        ) : (
          <span className="text-xs text-stone-400">Belum ada TTD</span>
        )}
      </div>
      <div className="flex-1 space-y-2">
        <p className="text-xs font-medium text-stone-700">{label}</p>
        <p className="text-xs text-stone-600">{desc}</p>
        <input id={`${prefix}-input`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSignature(f, role); e.target.value = ""; }} data-testid={`${prefix}-upload-input`} />
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => document.getElementById(`${prefix}-input`)?.click()} data-testid={`${prefix}-upload-btn`}>Unggah TTD</Button>
          {sigState.base64 && <Button size="sm" variant="outline" onClick={() => removeSignature(role)} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`${prefix}-remove-btn`}><Trash size={12} className="mr-1" />Hapus</Button>}
        </div>
        <p className="text-[11px] text-stone-500">PNG/JPG/WEBP maks 500 KB.</p>
      </div>
    </div>
  );

  const saveTax = async () => {
    try { await api.post("/efaktur/settings", { ...tax, default_vat_rate: Number(tax.default_vat_rate) }); toast.success("Pengaturan pajak tersimpan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const saveWh = async () => {
    try {
      if (whEdit) await api.put(`/warehouses/${whEdit.id}`, whForm);
      else await api.post("/warehouses", whForm);
      toast.success("Gudang tersimpan"); setWhOpen(false); setWhForm({ code: "", name: "", address: "", manager: "", is_default: false }); setWhEdit(null);
      api.get("/warehouses").then((r) => setWarehouses(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const delWh = async (id) => { if (!window.confirm("Hapus gudang?")) return; try { await api.delete(`/warehouses/${id}`); api.get("/warehouses").then((r) => setWarehouses(r.data)); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } };
  const editWh = (w) => { setWhEdit(w); setWhForm({ code: w.code, name: w.name, address: w.address || "", manager: w.manager || "", is_default: w.is_default || false }); setWhOpen(true); };

  const previewNumber = () => {
    const d = new Date();
    const y = d.getFullYear().toString();
    const parts = [];
    if (numForm.prefix) parts.push(numForm.prefix.replace(/-$/, ""));
    if (numForm.include_year) parts.push(numForm.year_format === "YYYY" ? y : y.slice(2));
    if (numForm.include_month) parts.push((d.getMonth() + 1).toString().padStart(2, "0"));
    if (numForm.include_day) parts.push(d.getDate().toString().padStart(2, "0"));
    parts.push(String(numForm.start).padStart(numForm.padding, "0"));
    return parts.join("-");
  };
  const saveNum = async () => {
    try {
      await api.put(`/numbering/${numEditing}`, { ...numForm, padding: Number(numForm.padding), start: Number(numForm.start) });
      toast.success("Format nomor tersimpan"); setNumEditing(null);
      api.get("/numbering").then((r) => { setNumSeries(r.data.series); setNumLabels(r.data.labels); });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const editNum = (key) => { setNumEditing(key); setNumForm({ ...numSeries[key] }); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="settings-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Pengaturan</h1>
        <p className="text-sm text-slate-500 mt-1">Info workspace, pajak, gudang, dan format nomor otomatis.</p>
      </div>

      <Tabs defaultValue="profile" className="mt-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profil Perusahaan</TabsTrigger>
          <TabsTrigger value="general" data-testid="tab-general">Umum</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax">Pajak (e-Faktur)</TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="tab-wh">Gudang</TabsTrigger>
          <TabsTrigger value="numbering" data-testid="tab-num">Nomor Otomatis</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Keamanan</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrasi</TabsTrigger>
          <TabsTrigger value="ai-jobdesk" data-testid="tab-ai-jobdesk">Job Desk AI</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4" data-testid="logo-card">
              <div className="flex items-center gap-2 text-stone-900">
                <ImageIcon size={20} weight="duotone" className="text-[color:var(--nusa-accent)]" />
                <h3 className="font-serif-display text-xl font-medium">Logo Perusahaan</h3>
              </div>
              <div className="aspect-square rounded-md border-2 border-dashed border-stone-300 flex items-center justify-center bg-stone-50 overflow-hidden">
                {logo.base64 ? (
                  <img src={`data:${logo.mime || "image/png"};base64,${logo.base64}`} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
                ) : logo.url ? (
                  <img src={logo.url} alt="Logo URL" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-center text-stone-400 text-xs px-4"><ImageIcon size={32} weight="duotone" className="mx-auto mb-2" />Belum ada logo</div>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} data-testid="logo-upload-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => logoInputRef.current?.click()} className="flex-1 bg-[color:var(--nusa-accent)] hover:bg-[color:var(--nusa-accent-hover)] text-white" data-testid="logo-upload-btn">Unggah Logo</Button>
                {(logo.base64 || logo.url) && <Button size="sm" variant="outline" onClick={removeLogo} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="logo-remove-btn"><Trash size={14} /></Button>}
              </div>
              <p className="text-[11px] text-stone-500 leading-relaxed">PNG/JPG/WEBP maks 1 MB. Logo ini akan otomatis muncul di <b>Faktur</b>, <b>Surat Jalan</b>, dan <b>Purchase Order</b>.</p>
              <p className="text-[11px] text-stone-500 leading-relaxed rounded bg-amber-50 border border-amber-200 p-2"><b>Rekomendasi ukuran:</b> rasio landscape sekitar <b>3:1</b> (mis. 900×300 px), latar transparan (PNG). Logo terlalu tinggi/persegi akan tampil kecil di header dokumen.</p>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="profile-form">
              <div className="flex items-center gap-2 text-stone-900 mb-4">
                <Buildings size={20} weight="duotone" className="text-[color:var(--nusa-accent)]" />
                <h3 className="font-serif-display text-xl font-medium">Identitas Perusahaan</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Nama Perusahaan</Label><Input value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} placeholder="PT Nama Perusahaan Anda" data-testid="profile-name" /></div>
                <div className="md:col-span-2"><Label>Tagline / Slogan (opsional)</Label><Input value={profile.header_tagline} onChange={(e) => setProfile({ ...profile, header_tagline: e.target.value })} placeholder="Solusi Terpercaya untuk Bisnis Anda" data-testid="profile-tagline" /></div>
                <div className="md:col-span-2"><Label>Alamat</Label><Input value={profile.company_address} onChange={(e) => setProfile({ ...profile, company_address: e.target.value })} placeholder="Jl. Sudirman No. 123, Jakarta Selatan 12190" data-testid="profile-address" /></div>
                <div><Label>Telepon</Label><Input value={profile.company_phone} onChange={(e) => setProfile({ ...profile, company_phone: e.target.value })} placeholder="+62 21 1234 5678" data-testid="profile-phone" /></div>
                <div><Label>Email</Label><Input value={profile.company_email} onChange={(e) => setProfile({ ...profile, company_email: e.target.value })} placeholder="info@perusahaan.com" data-testid="profile-email" /></div>
                <div className="md:col-span-2"><Label>Website</Label><Input value={profile.company_website} onChange={(e) => setProfile({ ...profile, company_website: e.target.value })} placeholder="www.perusahaan.com" data-testid="profile-website" /></div>
                <div className="md:col-span-2"><Label>Info Bank / No. Rekening (khusus Faktur Penjualan)</Label><Input value={profile.company_bank} onChange={(e) => setProfile({ ...profile, company_bank: e.target.value })} placeholder="BCA — 1234567890" data-testid="profile-bank" /></div>
                <div><Label>Rekening atas nama</Label><Input value={profile.bank_account_name} onChange={(e) => setProfile({ ...profile, bank_account_name: e.target.value })} placeholder="(default: nama perusahaan)" data-testid="profile-bank-acc-name" /></div>
                <div><Label>Kantor Cabang</Label><Input value={profile.bank_branch} onChange={(e) => setProfile({ ...profile, bank_branch: e.target.value })} placeholder="cth: KCP Jakarta Sudirman" data-testid="profile-bank-branch" /></div>
                <div className="md:col-span-2"><Label>Footer Dokumen (opsional)</Label><Input value={profile.footer_text} onChange={(e) => setProfile({ ...profile, footer_text: e.target.value })} placeholder="Terdaftar sebagai PKP — NPWP xx.xxx.xxx.x-xxx.000" data-testid="profile-footer" /></div>
                <div className="md:col-span-2 pt-2 border-t border-stone-100 mt-2">
                  <p className="text-xs font-medium text-stone-700 mb-2">Penandatangan Faktur</p>
                </div>
                <div><Label>Nama</Label><Input value={profile.signer_name} onChange={(e) => setProfile({ ...profile, signer_name: e.target.value })} placeholder="Nama lengkap" data-testid="profile-signer-name" /></div>
                <div><Label>Jabatan</Label><Input value={profile.signer_title} onChange={(e) => setProfile({ ...profile, signer_title: e.target.value })} placeholder="Manager Keuangan" data-testid="profile-signer-title" /></div>
                <SigBox sigState={sig} role="company" label="Tanda Tangan Perusahaan (Manager Keuangan)" desc="Muncul di blok 'Dengan Hormat' pada PDF Faktur." prefix="signature" />
                <div className="md:col-span-2 pt-2 border-t border-stone-100 mt-2">
                  <p className="text-xs font-medium text-stone-700 mb-2">Format Kop Surat / PDF</p>
                </div>
                <div><Label>Ukuran Kertas</Label>
                  <select className="mt-1 w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm" value={profile.paper_size} onChange={(e) => setProfile({ ...profile, paper_size: e.target.value })} data-testid="profile-paper-size">
                    <option value="A4">A4</option>
                    <option value="A5">A5</option>
                  </select>
                </div>
                <div><Label>Orientasi</Label>
                  <select className="mt-1 w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm" value={profile.paper_orientation} onChange={(e) => setProfile({ ...profile, paper_orientation: e.target.value })} data-testid="profile-paper-orientation">
                    <option value="portrait">Potrait (Berdiri)</option>
                    <option value="landscape">Landscape (Menyamping)</option>
                  </select>
                </div>
                <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-2 bg-stone-50 border border-stone-200 rounded-md p-3">
                  <p className="col-span-full text-xs text-stone-600 mb-1">Tampilkan pada kop surat:</p>
                  {[
                    ["show_address", "Alamat"],
                    ["show_phone", "Telepon"],
                    ["show_email", "Email"],
                    ["show_website", "Website"],
                    ["show_tax_id", "NPWP"],
                    ["show_bank", "Rekening Bank"],
                    ["show_footer", "Footer PKP"],
                  ].map(([key, label]) => (
                    <label key={key} className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!profile[key]} onChange={(e) => setProfile({ ...profile, [key]: e.target.checked })} data-testid={`profile-toggle-${key}`} />
                      {label}
                    </label>
                  ))}
                </div>
                <Button onClick={saveProfile} className="md:col-span-2 h-11 bg-stone-900 hover:bg-stone-800 text-white" data-testid="profile-save-btn">Simpan Profil Perusahaan</Button>
                <div className="md:col-span-2 pt-4 border-t border-stone-100 mt-4">
                  <p className="text-xs font-medium text-stone-700 mb-1">Ukuran Kertas per Dokumen (Override)</p>
                  <p className="text-[11px] text-stone-500 mb-3">Kosongkan untuk mengikuti default global di atas. Berguna misalnya jika Penawaran/SO/DO pakai A5 tapi Faktur pakai A4.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      ["sales-quotation", "Penawaran"],
                      ["sales-order", "Pesanan Penjualan"],
                      ["sales-delivery", "Pengiriman"],
                      ["sales-invoice", "Faktur Penjualan"],
                      ["sales-return", "Retur Penjualan"],
                      ["sales-receipt", "Penerimaan"],
                      ["sales-kwitansi", "Kwitansi LUNAS"],
                      ["purchase-requisition", "Permintaan"],
                      ["purchase-order", "PO"],
                      ["purchase-grn", "GRN"],
                      ["purchase-invoice", "Faktur Pembelian"],
                      ["purchase-return", "Retur Pembelian"],
                      ["purchase-payment", "Pembayaran"],
                    ].map(([k, label]) => {
                      const cfg = profile.paper_config?.[k] || {};
                      const update = (patch) => setProfile({
                        ...profile,
                        paper_config: { ...(profile.paper_config || {}), [k]: { ...cfg, ...patch } },
                      });
                      return (
                        <div key={k} className="flex items-center gap-2 text-sm bg-stone-50 border border-stone-200 rounded p-2">
                          <span className="flex-1 text-stone-700">{label}</span>
                          <select className="h-9 px-2 rounded-md border border-stone-200 bg-white text-xs" value={cfg.size || ""} onChange={(e) => update({ size: e.target.value })} data-testid={`paper-size-${k}`}>
                            <option value="">Default</option>
                            <option value="A4">A4</option>
                            <option value="A5">A5</option>
                          </select>
                          <select className="h-9 px-2 rounded-md border border-stone-200 bg-white text-xs" value={cfg.orientation || ""} onChange={(e) => update({ orientation: e.target.value })} data-testid={`paper-orient-${k}`}>
                            <option value="">Default</option>
                            <option value="portrait">Potrait</option>
                            <option value="landscape">Landscape</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-md bg-[color:var(--nusa-bg)] border border-stone-200 p-3 text-xs text-stone-600 leading-relaxed">
                <Info size={12} className="inline mr-1 text-[color:var(--nusa-accent)]" /> Data ini otomatis dipakai sebagai kop-surat di semua dokumen eksternal (Faktur, Surat Jalan, PO). Untuk NPWP resmi & data e-Faktur, isi di tab <b>Pajak</b>.
              </div>

              {/* ====== Data Korporasi — untuk Laporan Tahunan PT ====== */}
              <div className="mt-6 bg-white border border-stone-200 rounded-lg p-6" data-testid="corporate-data-form">
                <h3 className="font-serif-display text-xl font-medium mb-1 flex items-center gap-2"><FileText size={18} className="text-blue-700" />Data Korporasi untuk Laporan Tahunan PT</h3>
                <p className="text-xs text-stone-500 mb-4">Dipakai untuk membuat <b>Laporan Tahunan PT</b> (lampiran SPT Badan & update laporan penanaman modal). KBLI dan susunan Direksi/Komisaris akan tampil di PDF laporan tahunan.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Kedudukan (Kota)</Label><Input value={profile.company_city} onChange={(e) => setProfile({ ...profile, company_city: e.target.value })} placeholder="Jakarta Selatan" data-testid="profile-city" /></div>
                  <div><Label>Tanggal Pendirian</Label><Input type="date" value={profile.company_establishment_date} onChange={(e) => setProfile({ ...profile, company_establishment_date: e.target.value })} data-testid="profile-establishment-date" /></div>
                  <div className="md:col-span-2"><Label>Akta Pendirian (Notaris & Nomor)</Label><Input value={profile.company_establishment_notary} onChange={(e) => setProfile({ ...profile, company_establishment_notary: e.target.value })} placeholder="Akta Notaris Budi Santoso, S.H., No. 15 tanggal 10 Jan 2023" data-testid="profile-notary" /></div>
                  <div className="md:col-span-2"><Label>SK Menkumham</Label><Input value={profile.company_sk_menkumham} onChange={(e) => setProfile({ ...profile, company_sk_menkumham: e.target.value })} placeholder="SK Menkumham No. AHU-0001234.AH.01.01 Tahun 2023" data-testid="profile-sk" /></div>
                  <div className="md:col-span-2"><Label>Bidang Kegiatan Usaha</Label>
                    <textarea className="mt-1 w-full min-h-20 px-3 py-2 rounded-md border border-stone-200 bg-white text-sm" value={profile.business_activities} onChange={(e) => setProfile({ ...profile, business_activities: e.target.value })} placeholder="Perdagangan besar barang elektronik; jasa konsultasi teknologi informasi" data-testid="profile-activities" />
                  </div>
                </div>

                {/* KBLI */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Kode KBLI (Klasifikasi Baku Lapangan Usaha Indonesia)</Label>
                    <Button size="sm" variant="outline" onClick={addKbli} data-testid="kbli-add-btn"><Plus size={12} className="mr-1" />Tambah KBLI</Button>
                  </div>
                  {(profile.kbli_codes || []).length === 0 ? (
                    <p className="text-xs text-stone-400 italic">Belum ada kode KBLI. KBLI sesuai NIB/OSS perusahaan.</p>
                  ) : (
                    <div className="space-y-2">
                      {(profile.kbli_codes || []).map((k, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`kbli-row-${i}`}>
                          <Input className="w-28" placeholder="46900" value={k.code || ""} onChange={(e) => updKbli(i, { code: e.target.value })} data-testid={`kbli-code-${i}`} />
                          <Input className="flex-1" placeholder="Perdagangan Besar Berbagai Macam Barang" value={k.description || ""} onChange={(e) => updKbli(i, { description: e.target.value })} data-testid={`kbli-desc-${i}`} />
                          <Button size="sm" variant="ghost" onClick={() => delKbli(i)} data-testid={`kbli-del-${i}`}><Trash size={14} className="text-rose-600" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Direksi */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Susunan Direksi</Label>
                    <Button size="sm" variant="outline" onClick={() => addPengurus("directors", (profile.directors || []).length === 0 ? "Direktur Utama" : "Direktur")} data-testid="director-add-btn"><Plus size={12} className="mr-1" />Tambah Direktur</Button>
                  </div>
                  {(profile.directors || []).length === 0 ? (
                    <p className="text-xs text-stone-400 italic">Belum ada direksi.</p>
                  ) : (
                    <div className="space-y-2">
                      {(profile.directors || []).map((d, i) => (
                        <div key={i} className="grid md:grid-cols-4 gap-2 items-start bg-stone-50 border border-stone-200 rounded p-2" data-testid={`director-row-${i}`}>
                          <select className="h-9 px-2 rounded-md border border-stone-200 bg-white text-sm" value={d.title || "Direktur"} onChange={(e) => updPengurus("directors", i, { title: e.target.value })} data-testid={`director-title-${i}`}>
                            <option value="Direktur Utama">Direktur Utama</option>
                            <option value="Direktur">Direktur</option>
                            <option value="Direktur Operasional">Direktur Operasional</option>
                            <option value="Direktur Keuangan">Direktur Keuangan</option>
                          </select>
                          <Input placeholder="Nama lengkap" value={d.name || ""} onChange={(e) => updPengurus("directors", i, { name: e.target.value })} data-testid={`director-name-${i}`} />
                          <Input placeholder="No. KTP (opsional)" value={d.ktp || ""} onChange={(e) => updPengurus("directors", i, { ktp: e.target.value })} data-testid={`director-ktp-${i}`} />
                          <div className="flex gap-2">
                            <Input placeholder="Alamat (opsional)" value={d.address || ""} onChange={(e) => updPengurus("directors", i, { address: e.target.value })} data-testid={`director-addr-${i}`} />
                            <Button size="sm" variant="ghost" onClick={() => delPengurus("directors", i)} data-testid={`director-del-${i}`}><Trash size={14} className="text-rose-600" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Komisaris */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Susunan Dewan Komisaris</Label>
                    <Button size="sm" variant="outline" onClick={() => addPengurus("commissioners", (profile.commissioners || []).length === 0 ? "Komisaris Utama" : "Komisaris")} data-testid="commissioner-add-btn"><Plus size={12} className="mr-1" />Tambah Komisaris</Button>
                  </div>
                  {(profile.commissioners || []).length === 0 ? (
                    <p className="text-xs text-stone-400 italic">Belum ada komisaris.</p>
                  ) : (
                    <div className="space-y-2">
                      {(profile.commissioners || []).map((c, i) => (
                        <div key={i} className="grid md:grid-cols-4 gap-2 items-start bg-stone-50 border border-stone-200 rounded p-2" data-testid={`commissioner-row-${i}`}>
                          <select className="h-9 px-2 rounded-md border border-stone-200 bg-white text-sm" value={c.title || "Komisaris"} onChange={(e) => updPengurus("commissioners", i, { title: e.target.value })} data-testid={`commissioner-title-${i}`}>
                            <option value="Komisaris Utama">Komisaris Utama</option>
                            <option value="Komisaris">Komisaris</option>
                            <option value="Komisaris Independen">Komisaris Independen</option>
                          </select>
                          <Input placeholder="Nama lengkap" value={c.name || ""} onChange={(e) => updPengurus("commissioners", i, { name: e.target.value })} data-testid={`commissioner-name-${i}`} />
                          <Input placeholder="No. KTP (opsional)" value={c.ktp || ""} onChange={(e) => updPengurus("commissioners", i, { ktp: e.target.value })} data-testid={`commissioner-ktp-${i}`} />
                          <div className="flex gap-2">
                            <Input placeholder="Alamat (opsional)" value={c.address || ""} onChange={(e) => updPengurus("commissioners", i, { address: e.target.value })} data-testid={`commissioner-addr-${i}`} />
                            <Button size="sm" variant="ghost" onClick={() => delPengurus("commissioners", i)} data-testid={`commissioner-del-${i}`}><Trash size={14} className="text-rose-600" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-2 items-center">
                  <Button onClick={saveProfile} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="corporate-save-btn">Simpan Data Korporasi</Button>
                  <p className="text-xs text-stone-500 ml-auto">Unduh PDF Laporan Tahunan PT dari menu <b>Laporan → Laporan Tahunan PT</b>.</p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="general" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="workspace-info">
              <h3 className="font-serif-display text-2xl font-medium">Workspace</h3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Nama</dt><dd className="font-medium">{workspace?.name}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Plan</dt><dd className="uppercase font-medium text-amber-700">{workspace?.plan}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Mata Uang</dt><dd className="font-medium">{workspace?.settings?.currency || "IDR"}</dd></div>
              </dl>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="user-info">
              <h3 className="font-serif-display text-2xl font-medium">Akun Saya</h3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Nama</dt><dd className="font-medium">{user?.full_name}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="font-medium">{user?.email}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Peran</dt><dd className="capitalize font-medium">{user?.role}</dd></div>
              </dl>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-6 lg:col-span-2" data-testid="costing-method-card">
              <CostingMethodCard />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tax" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="tax-settings">
            <h3 className="font-serif-display text-2xl font-medium flex items-center gap-2"><FileText size={22} weight="duotone" className="text-amber-600" />Pengaturan Pajak (e-Faktur / CoreTax)</h3>
            <p className="text-xs text-slate-500 mt-1">Info NPWP perusahaan diperlukan untuk XML e-Faktur & e-Bupot ke DJP.</p>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Nama Badan (sesuai NPWP)</Label><Input value={tax.company_name} onChange={(e) => setTax({ ...tax, company_name: e.target.value })} data-testid="tax-company-input" /></div>
              <div><Label>NPWP (16-digit)</Label><Input value={tax.company_npwp} onChange={(e) => setTax({ ...tax, company_npwp: e.target.value })} maxLength={16} data-testid="tax-npwp-input" /></div>
              <div><Label>NITKU / IDTKU</Label><Input value={tax.company_idtku} onChange={(e) => setTax({ ...tax, company_idtku: e.target.value })} data-testid="tax-idtku-input" /><p className="text-[10px] text-slate-500 mt-1">Wajib diisi untuk XML BPMP payroll CoreTax (IDPlaceOfBusinessActivity).</p></div>
              <div><Label>Kode Transaksi Default</Label>
                <Select value={tax.default_trx_code} onValueChange={(v) => setTax({ ...tax, default_trx_code: v })}>
                  <SelectTrigger data-testid="tax-trx-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="01">01 – Selain Pemungut PPN</SelectItem>
                    <SelectItem value="02">02 – Bendaharawan</SelectItem>
                    <SelectItem value="03">03 – Selain Bendaharawan</SelectItem>
                    <SelectItem value="04">04 – DPP Nilai Lain</SelectItem>
                    <SelectItem value="07">07 – Tidak Dipungut</SelectItem>
                    <SelectItem value="08">08 – Dibebaskan</SelectItem>
                    <SelectItem value="09">09 – Aktiva (Pasal 16D)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tarif PPN Default (%)</Label><Input type="number" step="0.01" value={tax.default_vat_rate} onChange={(e) => setTax({ ...tax, default_vat_rate: e.target.value })} /></div>
              <div className="md:col-span-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3">
                <input type="checkbox" id="tax-dpp-nilai-lain" checked={!!tax.use_dpp_nilai_lain} onChange={(e) => setTax({ ...tax, use_dpp_nilai_lain: e.target.checked })} className="mt-0.5" data-testid="tax-dpp-nilai-lain-check" />
                <Label htmlFor="tax-dpp-nilai-lain" className="cursor-pointer text-xs leading-relaxed">
                  <span className="font-semibold">Gunakan DPP Nilai Lain (PMK 131/2024)</span><br />
                  <span className="text-slate-600">Menjadi default untuk Faktur Penjualan ke pelanggan PKP: PPN {Number(tax.default_vat_rate) || 12}% dihitung atas 11/12 × DPP (efektif 11% dari harga jual). Bisa diubah per faktur.</span>
                </Label>
              </div>
              <div className="md:col-span-2"><Label>Nama Penandatangan</Label><Input value={tax.signer_name} onChange={(e) => setTax({ ...tax, signer_name: e.target.value })} /></div>
              <Button onClick={saveTax} className="md:col-span-2 bg-slate-900" data-testid="tax-save-btn">Simpan Pengaturan Pajak</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="warehouses" className="mt-4">
          <div className="flex justify-between items-baseline mb-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-serif-display text-2xl font-medium flex items-center gap-2"><House size={22} weight="duotone" className="text-amber-600" />Gudang</h3>
              <Button variant="outline" size="sm" onClick={async () => { try { await exportExcel("warehouses", `gudang_${new Date().toISOString().slice(0,10)}.xlsx`); toast.success("Data gudang diekspor"); } catch (e) { toast.error(e.message); } }} data-testid="wh-export-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><DownloadSimple size={14} className="mr-1" />Export Excel</Button>
            </div>
            <Dialog open={whOpen} onOpenChange={(o) => { setWhOpen(o); if (!o) { setWhEdit(null); setWhForm({ code: "", name: "", address: "", manager: "", is_default: false }); } }}>
              <DialogTrigger asChild><Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-wh-btn"><Plus size={16} className="mr-1" />Gudang Baru</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>{whEdit ? "Edit" : "Tambah"} Gudang</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Kode</Label><Input value={whForm.code} onChange={(e) => setWhForm({ ...whForm, code: e.target.value.toUpperCase() })} placeholder="MAIN, JKT, SBY..." data-testid="wh-code-input" /></div>
                  <div><Label>Nama</Label><Input value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} data-testid="wh-name-input" /></div>
                  <div><Label>Alamat</Label><Input value={whForm.address} onChange={(e) => setWhForm({ ...whForm, address: e.target.value })} /></div>
                  <div><Label>Penanggung Jawab</Label><Input value={whForm.manager} onChange={(e) => setWhForm({ ...whForm, manager: e.target.value })} /></div>
                  <div className="flex items-center gap-2"><input type="checkbox" id="wh-def" checked={whForm.is_default} onChange={(e) => setWhForm({ ...whForm, is_default: e.target.checked })} data-testid="wh-default-check" /><Label htmlFor="wh-def">Jadikan Gudang Default</Label></div>
                  <Button onClick={saveWh} className="w-full bg-slate-900" data-testid="wh-save-btn">Simpan</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-slate-200 rounded-md" data-testid="wh-table">
            <Table>
              <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Alamat</TableHead><TableHead>PIC</TableHead><TableHead>Default</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {warehouses.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500 text-sm">Belum ada gudang.</TableCell></TableRow> : warehouses.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-mono-plex text-xs">{w.code}</TableCell>
                    <TableCell className="font-medium">{w.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{w.address || "—"}</TableCell>
                    <TableCell className="text-sm">{w.manager || "—"}</TableCell>
                    <TableCell>{w.is_default && <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Default</span>}</TableCell>
                    <TableCell className="flex gap-1">
                      <button onClick={() => editWh(w)} className="text-slate-400 hover:text-slate-900"><PencilSimple size={14} /></button>
                      {w.code !== "MAIN" && <button onClick={() => delWh(w.id)} className="text-slate-400 hover:text-rose-600"><Trash size={14} /></button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="numbering" className="mt-4">
          <h3 className="font-serif-display text-2xl font-medium flex items-center gap-2"><Hash size={22} weight="duotone" className="text-amber-600" />Format Nomor Otomatis</h3>
          <p className="text-xs text-slate-500 mt-1 mb-3">Atur prefix, penyertaan tahun/bulan, dan angka awal untuk setiap jenis dokumen. Perubahan berlaku untuk dokumen baru.</p>
          <div className="bg-white border border-slate-200 rounded-md" data-testid="num-table">
            <Table>
              <TableHeader><TableRow><TableHead>Dokumen</TableHead><TableHead>Prefix</TableHead><TableHead>Reset</TableHead><TableHead>Contoh</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {Object.entries(numSeries).map(([key, cfg]) => {
                  const preview = [
                    cfg.prefix?.replace(/-$/, ""),
                    cfg.include_year ? (cfg.year_format === "YYYY" ? "2026" : "26") : null,
                    cfg.include_month ? "02" : null,
                    cfg.include_day ? "15" : null,
                    String(cfg.start).padStart(cfg.padding, "0"),
                  ].filter(Boolean).join("-");
                  return (
                    <TableRow key={key}>
                      <TableCell className="text-sm">{numLabels[key] || key}</TableCell>
                      <TableCell className="font-mono-plex text-xs">{cfg.prefix}</TableCell>
                      <TableCell className="text-xs capitalize">{cfg.reset}</TableCell>
                      <TableCell className="font-mono-plex text-xs text-slate-600">{preview}</TableCell>
                      <TableCell><Button size="sm" variant="outline" onClick={() => editNum(key)} data-testid={`num-edit-${key}`}>Edit</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <Dialog open={!!numEditing} onOpenChange={(o) => { if (!o) setNumEditing(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Format Nomor: {numLabels[numEditing]}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Prefix</Label><Input value={numForm.prefix} onChange={(e) => setNumForm({ ...numForm, prefix: e.target.value })} placeholder="INV-" data-testid="num-prefix-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={numForm.include_year} onChange={(e) => setNumForm({ ...numForm, include_year: e.target.checked })} /><Label>Sertakan Tahun</Label></div>
                  <div><Label>Format Tahun</Label>
                    <Select value={numForm.year_format} onValueChange={(v) => setNumForm({ ...numForm, year_format: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="YYYY">YYYY (4-digit)</SelectItem><SelectItem value="YY">YY (2-digit)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={numForm.include_month} onChange={(e) => setNumForm({ ...numForm, include_month: e.target.checked })} /><Label>Sertakan Bulan</Label></div>
                  <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={numForm.include_day} onChange={(e) => setNumForm({ ...numForm, include_day: e.target.checked })} data-testid="num-include-day" /><Label>Sertakan Tanggal (DD)</Label></div>
                  <div><Label>Reset Sequence</Label>
                    <Select value={numForm.reset} onValueChange={(v) => setNumForm({ ...numForm, reset: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="never">Tidak Pernah</SelectItem><SelectItem value="annual">Tahunan</SelectItem><SelectItem value="monthly">Bulanan</SelectItem><SelectItem value="daily">Harian</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Padding Digit</Label><Input type="number" value={numForm.padding} onChange={(e) => setNumForm({ ...numForm, padding: e.target.value })} /></div>
                  <div><Label>Nomor Awal</Label><Input type="number" value={numForm.start} onChange={(e) => setNumForm({ ...numForm, start: e.target.value })} /></div>
                </div>
                <div className="bg-slate-50 rounded p-2 text-sm">Contoh: <b className="font-mono-plex">{previewNumber()}</b></div>
                <Button onClick={saveNum} className="w-full bg-slate-900" data-testid="num-save-btn">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <ChangePasswordSection user={user} />
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <div className="space-y-4">
            <GmailIntegrationSection />
            <ReminderSection />
          </div>
        </TabsContent>

        <TabsContent value="ai-jobdesk" className="mt-4">
          <AIJobDeskSection />
        </TabsContent>
      </Tabs>

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3 text-sm text-amber-900">
        <Info size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
        <div>
          Integrasi Telegram & Xendit dikonfigurasi di menu masing-masing. NSFP resmi di-assign CoreTax setelah XML diupload.
        </div>
      </div>
    </div>
  );
}


function ReminderSection() {
  const [cfg, setCfg] = useState({ enabled: false, days_before: 3, sender_user_id: "" });
  const [senders, setSenders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    api.get("/reminders/settings").then(r => setCfg(r.data)).catch(() => {});
    api.get("/reminders/senders").then(r => setSenders(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/reminders/settings", cfg); toast.success("Pengaturan pengingat tersimpan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reminders/run-now");
      const s = r.data;
      toast.success(`Uji selesai — dicek: ${s.checked}, terkirim: ${s.sent}, dilewati (tanpa email): ${s.skipped_no_email}, error: ${s.errors}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setRunning(false); }
  };
  const readySenders = senders.filter(s => s.channel);
  return (
    <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-6" data-testid="reminder-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bell size={20} weight="duotone" className="text-amber-600" />
        <h3 className="font-serif-display text-xl text-slate-900">Pengingat Jatuh Tempo Otomatis</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Sistem mengirim email pengingat otomatis ke pelanggan <b>setiap hari jam 08:00 WIB</b> untuk faktur yang akan jatuh tempo tepat H-(N) dan belum lunas. Email dikirim via SMTP/Gmail milik user pengirim yang dipilih. Setiap faktur hanya diingatkan sekali per tanggal jatuh tempo.
      </p>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input type="checkbox" id="rm-enabled" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="reminder-enabled-check" />
          <Label htmlFor="rm-enabled" className="font-medium">Aktifkan pengingat otomatis</Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Kirim H-berapa sebelum jatuh tempo</Label>
            <Select value={String(cfg.days_before)} onValueChange={(v) => setCfg({ ...cfg, days_before: Number(v) })}>
              <SelectTrigger data-testid="reminder-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 5, 7].map(d => <SelectItem key={d} value={String(d)}>H-{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>User Pengirim (SMTP/Gmail)</Label>
            <Select value={cfg.sender_user_id || ""} onValueChange={(v) => setCfg({ ...cfg, sender_user_id: v })}>
              <SelectTrigger data-testid="reminder-sender-select"><SelectValue placeholder={readySenders.length ? "Pilih user" : "Belum ada user terhubung"} /></SelectTrigger>
              <SelectContent>
                {readySenders.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.email} — {s.channel === "smtp" ? "SMTP" : "Gmail"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {readySenders.length === 0 && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
            Belum ada user yang menghubungkan SMTP/Gmail. Hubungkan dulu di kartu di atas, lalu kembali ke sini.
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-slate-900" data-testid="reminder-save-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
          <Button variant="outline" onClick={runNow} disabled={running || !cfg.enabled} data-testid="reminder-run-btn">{running ? "Memproses..." : "Jalankan Sekarang (Uji)"}</Button>
        </div>
      </div>
    </div>
  );
}

function SmtpConfigSection() {
  const empty = { mode: "gmail", email: "", password: "", host: "", port: 587, use_tls: true, from_name: "" };
  const [cfg, setCfg] = useState(empty);
  const [configured, setConfigured] = useState(false);
  const [hasPwd, setHasPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get("/smtp/config").then((r) => {
      const d = r.data;
      setConfigured(!!d.configured);
      setHasPwd(!!d.configured);
      if (d.configured) setCfg({ mode: d.mode, email: d.email, password: "", host: d.host, port: d.port, use_tls: d.use_tls, from_name: d.from_name });
    }).catch(() => {});
  }, []);
  const save = async () => {
    if (!cfg.email) return toast.error("Email pengirim wajib diisi");
    if (!cfg.password && !hasPwd) return toast.error("App Password / password wajib diisi");
    setSaving(true);
    try {
      await api.post("/smtp/config", cfg);
      toast.success("Konfigurasi SMTP tersimpan");
      setConfigured(true); setHasPwd(true); setCfg({ ...cfg, password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const r = await api.post("/smtp/test"); toast.success(`Email tes terkirim ke ${r.data.sent_to} — cek inbox Anda`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Tes gagal"); }
    finally { setTesting(false); }
  };
  const remove = async () => {
    if (!window.confirm("Hapus konfigurasi SMTP? Tombol 'Kirim via SMTP' akan nonaktif.")) return;
    try { await api.delete("/smtp/config"); toast.success("Konfigurasi dihapus"); setCfg(empty); setConfigured(false); setHasPwd(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <EnvelopeSimple size={20} weight="duotone" className="text-orange-600" />
        <h3 className="font-serif-display text-xl text-slate-900">Email SMTP — Alternatif Tanpa Google Cloud Console</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Kirim faktur langsung dari email Anda tanpa setup Google Cloud Console. Untuk <b>Gmail</b>: aktifkan 2-Step Verification lalu buat App Password di <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-amber-700 underline">myaccount.google.com/apppasswords</a> (16 karakter), paste di sini. Password disimpan terenkripsi di server.
      </p>
      {configured && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded p-2 mb-3 text-sm" data-testid="smtp-configured-card">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span>Aktif — <b data-testid="smtp-active-email">{cfg.email}</b> via {cfg.host}:{cfg.port}</span>
        </div>
      )}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Mode</Label>
            <Select value={cfg.mode} onValueChange={(v) => setCfg({ ...cfg, mode: v, host: v === "gmail" ? "smtp.gmail.com" : cfg.host, port: v === "gmail" ? 587 : cfg.port })}>
              <SelectTrigger data-testid="smtp-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gmail">Gmail (App Password)</SelectItem>
                <SelectItem value="custom">SMTP Lainnya (Zoho/Outlook/custom domain)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Email Pengirim</Label><Input type="email" value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} placeholder="anda@gmail.com" data-testid="smtp-email-input" /></div>
          <div className="col-span-2"><Label>{cfg.mode === "gmail" ? "App Password Gmail (16 karakter)" : "Password SMTP"}{hasPwd && <span className="text-slate-400 font-normal"> — tersimpan, kosongkan jika tidak diubah</span>}</Label>
            <Input type="password" value={cfg.password} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} placeholder={hasPwd ? "••••••••••••••••" : "abcd efgh ijkl mnop"} data-testid="smtp-password-input" autoComplete="new-password" /></div>
          {cfg.mode === "custom" && (
            <>
              <div><Label>Host SMTP</Label><Input value={cfg.host} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} placeholder="smtp.zoho.com" data-testid="smtp-host-input" /></div>
              <div><Label>Port</Label><Input type="number" value={cfg.port} onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })} data-testid="smtp-port-input" /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={cfg.use_tls} onChange={(e) => setCfg({ ...cfg, use_tls: e.target.checked })} data-testid="smtp-tls-check" /><Label>STARTTLS / TLS</Label></div>
            </>
          )}
          <div><Label>Nama Pengirim (opsional)</Label><Input value={cfg.from_name} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} placeholder="Finance PT Nusa" data-testid="smtp-fromname-input" /></div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="smtp-save-btn">{saving ? "Menyimpan..." : "Simpan Konfigurasi"}</Button>
          {configured && <Button variant="outline" onClick={test} disabled={testing} data-testid="smtp-test-btn">{testing ? "Mengirim tes..." : "Kirim Email Tes"}</Button>}
          {configured && <Button variant="outline" onClick={remove} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="smtp-delete-btn">Hapus</Button>}
        </div>
      </div>
    </div>
  );
}

function CostingMethodCard() {
  const [method, setMethod] = useState("average");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/inventory/costing-method").then((r) => setMethod(r.data.method || "average")).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const save = async (v) => {
    setMethod(v);
    try { await api.put("/inventory/costing-method", { method: v }); toast.success(`Metode diubah ke ${v.toUpperCase()}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <>
      <h3 className="font-serif-display text-2xl font-medium">Metode Penilaian Persediaan</h3>
      <p className="text-xs text-slate-500 mt-1 mb-3">Menentukan cara Harga Beli (HPP) barang di-update saat pembelian & dicatat saat penjualan. Berlaku untuk seluruh gudang.</p>
      {loading ? <div className="text-sm text-slate-500">Memuat...</div> : (
        <div className="grid grid-cols-2 gap-3">
          <label className={`cursor-pointer border rounded p-3 ${method === "average" ? "border-amber-500 bg-amber-50" : "border-slate-200"}`} data-testid="costing-average">
            <input type="radio" checked={method === "average"} onChange={() => save("average")} className="mr-2" />
            <b>Rata-Rata Tertimbang (AVERAGE)</b>
            <p className="text-xs text-slate-500 mt-1">HPP di-update jadi rata-rata tertimbang stok lama + pembelian baru. Cocok untuk barang standar & fluktuasi harga wajar.</p>
          </label>
          <label className={`cursor-pointer border rounded p-3 ${method === "fifo" ? "border-amber-500 bg-amber-50" : "border-slate-200"}`} data-testid="costing-fifo">
            <input type="radio" checked={method === "fifo"} onChange={() => save("fifo")} className="mr-2" />
            <b>FIFO (First-In First-Out)</b>
            <p className="text-xs text-slate-500 mt-1">Barang yang lebih dulu masuk, lebih dulu keluar. HPP saat jual mengikuti harga batch terlama. Cocok untuk perishable & tuntutan pajak PSAK.</p>
          </label>
        </div>
      )}
    </>
  );
}

function GmailIntegrationSection() {
  const [status, setStatus] = useState({ connected: false, email: "" });
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [tplDlg, setTplDlg] = useState(null); // template obj utk edit, atau {name:'',subject:'',body:''} untuk baru
  const load = () => api.get("/oauth/gmail/status").then(r => setStatus(r.data)).finally(() => setLoading(false));
  const loadTpls = () => api.get("/gmail-templates").then(r => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadTpls();
    const p = new URLSearchParams(window.location.search);
    if (p.get("gmail") === "connected") {
      toast.success(`Gmail terhubung: ${p.get("email") || ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (p.get("gmail") === "error") {
      toast.error(`Gagal hubungkan Gmail: ${p.get("msg") || ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  const connect = () => {
    const token = localStorage.getItem("nusa_token");
    if (!token) return toast.error("Sesi tidak valid, login ulang");
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/gmail/connect?token=${encodeURIComponent(token)}`;
  };
  const disconnect = async () => {
    if (!window.confirm("Putuskan koneksi Gmail? Tombol 'Kirim via Gmail' pada faktur akan nonaktif hingga terhubung lagi.")) return;
    try { await api.post("/oauth/gmail/disconnect"); toast.success("Gmail terputus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const saveTpl = async () => {
    if (!tplDlg.name || !tplDlg.subject || !tplDlg.body) return toast.error("Nama, subjek, dan isi wajib diisi");
    try {
      if (tplDlg.id && !String(tplDlg.id).startsWith("builtin-")) {
        await api.put(`/gmail-templates/${tplDlg.id}`, { name: tplDlg.name, subject: tplDlg.subject, body: tplDlg.body });
      } else {
        await api.post("/gmail-templates", { name: tplDlg.name, subject: tplDlg.subject, body: tplDlg.body });
      }
      toast.success("Template tersimpan"); setTplDlg(null); loadTpls();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const delTpl = async (t) => {
    if (!window.confirm(`Hapus template "${t.name}"?`)) return;
    try { await api.delete(`/gmail-templates/${t.id}`); toast.success("Template dihapus"); loadTpls(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="gmail-integration-panel">
        <div className="flex items-center gap-2 mb-4">
          <EnvelopeSimple size={22} weight="duotone" className="text-amber-600" />
          <h3 className="font-serif-display text-2xl text-slate-900">Gmail — Kirim Faktur via Email Anda</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Sambungkan akun Gmail Anda supaya faktur & kwitansi dikirim langsung dari alamat email Anda sendiri (bukan dari server AIAkuntan). Attachment PDF invoice otomatis disertakan. Cakupan izin hanya <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">gmail.send</code> — kami tidak bisa membaca email Anda.
        </p>
        {loading ? (
          <div className="text-sm text-slate-500">Memuat status...</div>
        ) : status.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="gmail-connected-card">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <div className="flex-1 text-sm">
                <div className="text-slate-500 text-xs">Terhubung sebagai</div>
                <div className="font-medium text-slate-900" data-testid="gmail-email">{status.email || "(email tidak diketahui)"}</div>
              </div>
              <Button variant="outline" size="sm" onClick={disconnect} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="gmail-disconnect-btn">Putuskan</Button>
            </div>
            <div className="text-xs text-slate-500">Tombol <b>Kirim via Gmail</b> kini aktif di setiap baris Faktur (menu Penjualan).</div>
          </div>
        ) : (
          <Button onClick={connect} className="bg-amber-600 hover:bg-amber-700" data-testid="gmail-connect-btn">
            <EnvelopeSimple size={16} className="mr-2" weight="bold" />Sambungkan Gmail
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="smtp-panel">
        <SmtpConfigSection />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="tpl-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><FileText size={20} weight="duotone" className="text-amber-600" />
            <h3 className="font-serif-display text-xl text-slate-900">Template Pesan Email</h3>
          </div>
          <Button size="sm" onClick={() => setTplDlg({ name: "", subject: "", body: "" })} className="bg-amber-600 hover:bg-amber-700" data-testid="tpl-new-btn"><Plus size={14} className="mr-1" />Baru</Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Dipakai di dropdown "Gunakan Template" pada dialog Gmail. Placeholder yang tersedia: <code className="bg-slate-100 px-1">{"{invoice_number}"}</code>, <code className="bg-slate-100 px-1">{"{invoice_date}"}</code>, <code className="bg-slate-100 px-1">{"{due_date}"}</code>, <code className="bg-slate-100 px-1">{"{total}"}</code>, <code className="bg-slate-100 px-1">{"{partner_name}"}</code>, <code className="bg-slate-100 px-1">{"{company_name}"}</code>.</p>
        <div className="border border-slate-200 rounded divide-y" data-testid="tpl-list">
          {templates.length === 0 && <div className="p-3 text-sm text-slate-500">Belum ada template.</div>}
          {templates.map((t) => (
            <div key={t.id} className="p-3 flex items-start justify-between gap-3" data-testid={`tpl-row-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500 truncate">{t.subject}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setTplDlg(t)} data-testid={`tpl-edit-${t.id}`}><PencilSimple size={12} /></Button>
              <Button size="sm" variant="outline" onClick={() => delTpl(t)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`tpl-del-${t.id}`}><Trash size={12} /></Button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!tplDlg} onOpenChange={(o) => { if (!o) setTplDlg(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{tplDlg?.id ? "Edit Template" : "Template Baru"}</DialogTitle></DialogHeader>
          {tplDlg && (
            <div className="space-y-3">
              <div><Label>Nama</Label><Input value={tplDlg.name} onChange={(e) => setTplDlg({ ...tplDlg, name: e.target.value })} placeholder="Formal, Friendly, ..." data-testid="tpl-name-input" /></div>
              <div><Label>Subjek</Label><Input value={tplDlg.subject} onChange={(e) => setTplDlg({ ...tplDlg, subject: e.target.value })} placeholder="Faktur {invoice_number} dari {company_name}" data-testid="tpl-subject-input" /></div>
              <div><Label>Isi Pesan</Label>
                <textarea rows={9} value={tplDlg.body} onChange={(e) => setTplDlg({ ...tplDlg, body: e.target.value })}
                  className="w-full border border-slate-300 rounded-md p-2 text-sm font-mono" data-testid="tpl-body-input" />
              </div>
              <Button onClick={saveTpl} className="w-full bg-slate-900" data-testid="tpl-save-btn">Simpan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangePasswordSection({ user }) {
  const [form, setForm] = React.useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = React.useState(false);
  const isGoogle = user?.provider === "google";

  const submit = async () => {
    if (form.new_password.length < 6) { toast.error("Kata sandi baru minimal 6 karakter."); return; }
    if (form.new_password !== form.confirm_password) { toast.error("Konfirmasi kata sandi tidak cocok."); return; }
    setSaving(true);
    try {
      await api.put("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Kata sandi berhasil diubah. Silakan gunakan yang baru saat login berikutnya.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal ubah kata sandi");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg bg-white border border-slate-200 rounded-md p-6" data-testid="security-panel">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={20} weight="fill" className="text-amber-600" />
        <h3 className="font-serif-display text-2xl text-slate-900">Ubah Kata Sandi</h3>
      </div>

      {isGoogle ? (
        <div className="bg-slate-50 border border-slate-200 rounded p-4 text-sm text-slate-600" data-testid="security-google-notice">
          Akun ini login via <b>Google</b>. Ubah kata sandi dari Google Account Anda, bukan di sini.
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-4">
            Untuk keamanan, kami sarankan ganti kata sandi default segera setelah login pertama. Gunakan minimal 6 karakter.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Kata Sandi Saat Ini</Label>
              <Input type="password" autoComplete="current-password" value={form.current_password}
                     onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                     data-testid="pwd-current" />
            </div>
            <div>
              <Label className="text-xs">Kata Sandi Baru</Label>
              <Input type="password" autoComplete="new-password" value={form.new_password}
                     onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                     data-testid="pwd-new" />
              <div className="text-[11px] text-slate-400 mt-1">Minimal 6 karakter. Kombinasi huruf & angka lebih baik.</div>
            </div>
            <div>
              <Label className="text-xs">Konfirmasi Kata Sandi Baru</Label>
              <Input type="password" autoComplete="new-password" value={form.confirm_password}
                     onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                     data-testid="pwd-confirm" />
            </div>
            <Button onClick={submit} disabled={saving || !form.current_password || !form.new_password}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-2" data-testid="pwd-save-btn">
              {saving ? "Menyimpan..." : "Ubah Kata Sandi"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function AIJobDeskSection() {
  const [cfg, setCfg] = useState({ role: "admin", updated_at: null });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState("");
  const [logs, setLogs] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  useEffect(() => {
    api.get("/ai/jobdesk/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/ai/jobdesk/logs?limit=20").then((r) => setLogs(r.data)).catch(() => {});
  }, []);
  const save = async (role) => {
    setSaving(true);
    try {
      const r = await api.put("/ai/jobdesk/config", { role });
      setCfg({ ...cfg, role: r.data.role, updated_at: new Date().toISOString() });
      toast.success(`Peran AI diubah ke: ${role === "accounting" ? "Accounting" : "Admin"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  const runNow = async (kind) => {
    setRunning(kind); setLastRun(null);
    try {
      const url = kind === "sales" ? "/ai/jobdesk/run/daily-sales-report" : kind === "taxguard" ? "/ai/jobdesk/run/tax-guard" : "/ai/jobdesk/run/overdue-reminders";
      const r = await api.post(url, {});
      setLastRun({ kind, ...r.data });
      if (r.data.skipped) toast.message(`Dilewati: ${r.data.skipped}`);
      else toast.success(`Selesai dijalankan`);
      api.get("/ai/jobdesk/logs?limit=20").then((x) => setLogs(x.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menjalankan"); }
    finally { setRunning(""); }
  };
  const Role = ({ value, title, desc, pillColor }) => (
    <label className={`block border rounded-lg p-4 cursor-pointer transition ${cfg.role === value ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
           data-testid={`jobdesk-role-${value}`}>
      <div className="flex items-start gap-3">
        <input type="radio" name="aijobdesk" checked={cfg.role === value}
               onChange={() => save(value)} disabled={saving} className="mt-1" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900">{title}</span>
            <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${pillColor}`}>{value === "accounting" ? "Otonom" : "Perlu Konfirmasi"}</span>
          </div>
          <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{desc}</div>
        </div>
      </div>
    </label>
  );
  return (
    <div className="space-y-4" data-testid="ai-jobdesk-section">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-1">
          <Bell size={18} className="text-amber-600" />
          <h3 className="text-lg font-semibold">Job Desk AI</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Atur peran AI (chat aplikasi &amp; bot Telegram) untuk workspace ini. Perubahan berlaku langsung.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <Role value="admin" title="Admin"
                pillColor="bg-slate-100 text-slate-700"
                desc={"AI hanya menyiapkan dokumen (Penawaran, SO, Faktur Penjualan, PO, Faktur Pembelian, Expense, Uang Masuk, Uang Keluar).\nSemua transaksi WAJIB dikonfirmasi oleh pengguna (preview + tombol ✅/❌)."} />
          <Role value="accounting" title="Accounting"
                pillColor="bg-emerald-100 text-emerald-700"
                desc={"Semua kemampuan Admin + aksi otonom tanpa konfirmasi untuk tugas pembukuan:\n• Auto-buat mata akun COA bila belum ada\n• Laporan Penjualan Harian via Telegram (CRON 08:00 WIB)\n• Pengingat faktur LEWAT jatuh tempo ke pelanggan + ringkasan ke owner (CRON 09:00 WIB)\n• AI Tax Guard: pengingat jatuh tempo setor/lapor PPh & PPN — info H-14, reminder H-7, H-3, H-1 via Telegram & WhatsApp (CRON harian)"} />
        </div>
        {cfg.updated_at && (
          <div className="text-[11px] text-slate-400 mt-3">Terakhir diubah: {new Date(cfg.updated_at).toLocaleString("id-ID")}</div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h4 className="font-semibold mb-1">Jalankan Sekarang (uji / manual)</h4>
        <p className="text-xs text-slate-500 mb-3">
          Tombol ini mem-bypass filter peran — berguna untuk uji coba meski peran saat ini masih <b>Admin</b>.
          Untuk Laporan Harian, chat Telegram wajib terhubung. Untuk Pengingat Overdue, pengirim email wajib diset di tab Integrasi.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => runNow("sales")} disabled={!!running}
                  data-testid="run-daily-sales-btn">
            {running === "sales" ? "Menjalankan…" : "📊 Laporan Penjualan Kemarin"}
          </Button>
          <Button variant="outline" onClick={() => runNow("overdue")} disabled={!!running}
                  data-testid="run-overdue-btn">
            {running === "overdue" ? "Menjalankan…" : "🔔 Pengingat Overdue"}
          </Button>
          <Button variant="outline" onClick={() => runNow("taxguard")} disabled={!!running}
                  data-testid="run-taxguard-btn">
            {running === "taxguard" ? "Menjalankan…" : "🛡️ AI Tax Guard (PPh/PPN)"}
          </Button>
        </div>
        {lastRun && (
          <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700 space-y-0.5" data-testid="jobdesk-last-run">
            {lastRun.skipped ? (
              <div className="text-amber-700">Dilewati: <code>{lastRun.skipped}</code></div>
            ) : lastRun.kind === "sales" ? (
              <>
                <div>Tanggal: <b>{lastRun.date}</b></div>
                <div>Faktur: <b>{lastRun.invoices}</b> · Total Penjualan: <b>Rp {Number(lastRun.total_sales || 0).toLocaleString("id-ID")}</b></div>
                <div>Outstanding: Rp {Number(lastRun.outstanding || 0).toLocaleString("id-ID")}</div>
                <div>Terkirim ke Telegram: <b>{lastRun.sent ? "✅ Ya" : "❌ Tidak"}</b>{lastRun.error ? ` — ${lastRun.error}` : ""}</div>
              </>
            ) : lastRun.kind === "taxguard" ? (
              <>
                <div>Tanggal: <b>{lastRun.date}</b></div>
                <div>Deadline cocok (H-14/H-7/H-3/H-1): <b>{lastRun.matched}</b></div>
                <div>Telegram: <b>{lastRun.telegram_sent ? "✅ Terkirim" : "—"}</b> · WhatsApp: <b>{lastRun.whatsapp_sent ? "✅ Terkirim" : "—"}</b></div>
                {!lastRun.telegram_sent && !lastRun.whatsapp_sent && lastRun.matched > 0 && (
                  <div className="text-amber-700">Telegram/WhatsApp belum terhubung — hubungkan di tab Integrasi agar pengingat terkirim.</div>
                )}
              </>
            ) : (
              <>
                <div>Tanggal: <b>{lastRun.date}</b></div>
                <div>Faktur overdue ditemukan: <b>{lastRun.overdue_found}</b></div>
                <div>Email terkirim: <b className="text-emerald-700">{lastRun.sent}</b> · Dilewati (tanpa email): {lastRun.skipped_no_email} · Error: {lastRun.errors}</div>
                <div>Ringkasan Telegram ke owner: <b>{lastRun.telegram_sent ? "✅ Terkirim" : "— tidak terkirim"}</b></div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h4 className="font-semibold mb-2">Audit Trail AI Jobdesk</h4>
        {logs.length === 0 ? (
          <div className="text-sm text-slate-500">Belum ada aktivitas.</div>
        ) : (
          <div className="divide-y divide-slate-100 text-xs">
            {logs.map((l) => (
              <div key={l.id} className="py-2 flex items-start gap-3">
                <div className="w-32 shrink-0 text-slate-400">{new Date(l.created_at).toLocaleString("id-ID")}</div>
                <div className="w-32 shrink-0">
                  <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">{l.kind}</span>
                </div>
                <div className="flex-1 text-slate-700">{l.summary}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
